import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import ContinueWithGoogleButton from '@/components/functional/Oauth/ContinueWithGoogleButton/ContinueWithGoogleButton.tsx';
import ContinueWithFacebookButton from '@/components/functional/Oauth/ContinueWithFacebookButton/ContinueWithFacebookButton.tsx';
import ContinueWithAppleButton from '@/components/functional/Oauth/ContinueWithAppleButton/ContinueWithAppleButton.tsx';

const loginSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
  password: z.string().min(1, { message: 'This field is required.' }),
});

type LoginSchema = z.infer<typeof loginSchema>;

const LoginPage: FC = () => {
  const { control, handleSubmit } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { t } = useTranslation();
  const {
    loginQuery: { mutateAsync: login, isPending: isLoadingLogin, error: errorLogin, isError: isErrorLogin },
  } = useAuthContext();

  const handleLogin = (data: LoginSchema) => {
    void (async () => {
      try {
        if (isLoadingLogin) return;

        await login({
          email: data.email,
          password: data.password,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-[428px] max-w-full flex flex-col gap-4" onSubmit={handleSubmit(handleLogin)}>
        <div className="flex flex-col gap-4">
          {isErrorLogin && <Alert status="error">{errorLogin?.message}</Alert>}
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                type={'password'}
                placeholder={t('placeholder.password')}
              />
            )}
          />
          <LoadingButton
            loading={isLoadingLogin}
            type="submit"
            color="primary"
            className="rounded-full w-full pt-1 text-primary-text"
          >
            {t('button.login')}
          </LoadingButton>
          {import.meta.env.VITE_GOOGLE_LOGIN_ENABLED === 'true' && <ContinueWithGoogleButton />}
          {import.meta.env.VITE_FACEBOOK_LOGIN_ENABLED === 'true' && <ContinueWithFacebookButton />}
          {import.meta.env.VITE_APPLE_LOGIN_ENABLED === 'true' && <ContinueWithAppleButton />}
        </div>
        <div className="flex justify-end">
          <Link to={Routes.REGISTER}>
            <Button type="button" color="ghost">
              {t('button.createNewAccount')}
            </Button>
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default LoginPage;
